import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useFadeIn from '../hooks/useFadeIn';
import SlideDeckViewer from './SlideDeckViewer';
import { buildSignupPath, buildSignupState, captureCtaClick } from '../utils/analytics';

const PDF_SLIDES_URL = '/m3p-slides.pdf';
const PDF_SLIDE_COUNT = 24;

const faqs = [
  {
    q: 'How is the M3P bill paid? Do patients receive a monthly statement, or is it taken out of Social Security?',
    a: [
      'Participants receive a monthly standalone billing statement with itemized OOP costs by prescription, balances carried over from prior months, missed payments, and plan contact information.',
      'CMS encourages flexibility — payments via EFT, credit/debit cards, or checks. Cash payments are not required to be accepted.',
      'M3P payments are NOT automatically deducted from Social Security benefits. Participants must pay directly through their Part D sponsor.',
    ],
  },
  {
    q: 'What happens if a patient defaults on payments? How will this affect their ability to fill prescriptions?',
    a: [
      'CMS guidance prohibits Part D sponsors from denying access to medications due to unpaid M3P balances — prescription access is unaffected as long as premiums are paid.',
      'Sponsors will continue billing for unpaid amounts. Aggressive debt collection is prohibited; all federal and state debt collection laws apply.',
      'Participants can continue paying balances in monthly installments or as a lump sum.',
    ],
  },
  {
    q: 'If a patient has a late payment, will there be any late fees or penalties?',
    a: [
      'No. CMS explicitly prohibits Part D sponsors from charging late fees, interest, or any other penalties for missed or late payments. This prohibition extends to third-party contractors.',
    ],
  },
  {
    q: 'How can pharmacies and healthcare providers confirm whether a patient is enrolled in M3P?',
    a: [
      'Enrollment is identified through the claims processing system using a unique Bank Identification Number (BIN) and/or Processor Control Number (PCN) specific to M3P.',
      'Pharmacies and providers can also contact the Part D sponsor directly to verify enrollment status.',
    ],
  },
  {
    q: 'Is M3P enrollment available 24/7, or only on weekdays?',
    a: [
      'M3P enrollment is available 24 hours a day, 365 days a year. Processing typically takes 24 hours; enrollment becomes effective at the start of the next calendar month.',
    ],
  },
  {
    q: 'Does spreading the $2,000 cost over 12 months delay reaching catastrophic coverage?',
    a: [
      'No. M3P does not affect the coverage stage. The full cost still counts toward TrOOP as it is incurred, not when it is paid — so participation in M3P does not delay catastrophic coverage eligibility.',
    ],
  },
  {
    q: 'Does the patient need to pay their deductible before becoming eligible for M3P?',
    a: [
      'No. Patients can enroll in M3P at any time. If enrolled, deductible costs are also spread into monthly installments — patients do not need to satisfy the deductible first.',
    ],
  },
  {
    q: 'Will insulin products still have a $35 monthly cap under M3P?',
    a: [
      'Yes. Insulin products covered by Part D retain the $35 monthly cap, and those costs count toward the $2,000 cap. The $35 monthly insulin payment is spread over 12 months as part of M3P.',
    ],
  },
  {
    q: 'Are premiums kept separate from M3P payments?',
    a: [
      'Yes. Part D premiums are entirely separate from M3P payments. Patients receive two separate bills: one for M3P monthly payments, and one for plan premiums (if not deducted from Social Security).',
    ],
  },
  {
    q: 'Do CGM (continuous glucose monitor) copays under Part B count toward the M3P $2,000 cap?',
    a: [
      'No. Part B items such as CGMs do not count toward the $2,000 M3P cap. The cap applies exclusively to Part D covered drugs.',
    ],
  },
  {
    q: 'Are all pharmacies — including small local pharmacies — required to participate in M3P?',
    a: [
      'Yes. All network pharmacies associated with Part D sponsors must process M3P claims, including small local pharmacies. Pharmacies use M3P-specific BIN/PCN identifiers during claims adjudication.',
    ],
  },
  {
    q: 'Can patients choose which prescriptions are included in M3P?',
    a: [
      'No. Once enrolled, M3P applies to all covered Part D drugs. Patients cannot selectively include or exclude specific medications. Non-formulary or Part B drugs are excluded from M3P.',
    ],
  },
  {
    q: 'How does M3P interact with state-sponsored co-pay subsidy programs (e.g., New York)?',
    a: [
      'M3P monthly payments are treated as installments toward total OOP costs, not directly classified as co-pays or co-insurance. Coordination with state programs may require the sponsor to categorize M3P payments accordingly.',
      'For patients already receiving subsidies or co-pay assistance (including 340B pharmacies and foundation grants), enrolling in M3P is generally not recommended — it can complicate billing and reimbursement.',
    ],
  },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#e5e5ea] last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-4 py-5 text-left"
      >
        <span className="text-[15px] font-medium text-[#1d1d1f] leading-snug">{q}</span>
        <svg
          className={`w-4 h-4 flex-shrink-0 mt-0.5 text-[#86868b] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <ul className="pb-5 space-y-2 pl-0 list-none">
          {a.map((item, i) => (
            <li key={i} className="text-[14px] text-[#86868b] leading-relaxed pl-4 border-l-2 border-[#e5e5ea]">
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FormulaCard({ title, formula }) {
  return (
    <div className="rounded-2xl border border-[#e5e5ea] bg-[#f5f5f7] p-6">
      <p className="text-xs font-semibold text-[#86868b] uppercase tracking-widest mb-2">{title}</p>
      <p className="text-[15px] text-[#1d1d1f] leading-relaxed font-mono">{formula}</p>
    </div>
  );
}

export default function M3PPage() {
  const [heroRef, heroVisible] = useFadeIn(0.05);
  const [bodyRef, bodyVisible] = useFadeIn(0.06);

  return (
    <div className="bg-white">

      {/* ── Breadcrumb + Hero ─────────────────────── */}
      <section className="bg-[#f5f5f7] px-8 md:px-16 pt-14 pb-8">
        <div
          ref={heroRef}
          className={`fade-in-section ${heroVisible ? 'is-visible' : ''} max-w-[860px] mx-auto`}
        >
          <nav className="flex items-center gap-2 text-xs text-[#86868b] mb-6">
            <Link to="/resources" className="hover:text-[#1d1d1f] transition-colors">Resources</Link>
            <span>/</span>
            <span className="text-[#1d1d1f]">M3P Overview</span>
          </nav>
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700">Clinical Guide</span>
            <span className="text-xs text-[#86868b]">Updated Jan 2026</span>
            <span className="text-xs text-[#86868b]">·</span>
            <span className="text-xs text-[#86868b]">15 min read</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold text-[#1d1d1f] tracking-tight leading-tight mb-4">
            Medicare Prescription Payment Plan (M3P): Overview & FAQs
          </h1>
          <p className="text-[16px] text-[#86868b] leading-relaxed max-w-2xl">
            Starting January 1, 2025, all Medicare Part D plans must offer M3P — allowing patients to spread prescription costs into affordable monthly payments instead of large lump sums.
          </p>
        </div>
      </section>

      <SlideDeckViewer
        title="Slide Deck: M3P Quick Hits"
        description="Review the practical M3P workflow as a presentation, with fast slide navigation and a direct PDF fallback."
        pdfUrl={PDF_SLIDES_URL}
        slideImageBasePath="/slide-decks/m3p"
        thumbnailImageBasePath="/slide-decks/m3p/thumbs"
        pageCount={PDF_SLIDE_COUNT}
        analyticsEventName="m3p-open-pdf"
        analyticsPlacement="m3p-resource-page"
        className="bg-white px-6 py-10 md:px-16"
      />

      {/* ── Body ──────────────────────────────────── */}
      <section
        ref={bodyRef}
        className={`fade-in-section ${bodyVisible ? 'is-visible' : ''} px-8 md:px-16 pt-6 pb-16 max-w-[860px] mx-auto`}
      >

        {/* Overview */}
        <div className="mb-12 space-y-6">
          <h2 className="text-xl font-semibold text-[#1d1d1f]">Overview</h2>

          {[
            {
              label: 'Who is Eligible?',
              content: 'All Medicare Part D patients, including those in Prescription Drug Plans (PDP) and Medicare Advantage Prescription Drug Plans (MAPD). M3P applies at all pharmacy types.',
            },
            {
              label: 'What Does M3P Offer?',
              content: 'No upfront pharmacy costs — participants pay $0 at the pharmacy for covered medications. Prescription costs are then billed monthly by the patient\'s Medicare Part D plan.',
            },
          ].map(({ label, content }) => (
            <div key={label}>
              <h3 className="text-[15px] font-semibold text-[#1d1d1f] mb-1">{label}</h3>
              <p className="text-[15px] text-[#86868b] leading-relaxed">{content}</p>
            </div>
          ))}

          <div>
            <h3 className="text-[15px] font-semibold text-[#1d1d1f] mb-2">What M3P Does NOT Do</h3>
            <ul className="space-y-1.5">
              {[
                'Save money on total prescription costs.',
                'Change how patients progress through Medicare coverage phases.',
                'Alter what counts toward the True Out-of-Pocket (TrOOP) spending maximum ($2,000).',
              ].map((item) => (
                <li key={item} className="flex gap-2 text-[15px] text-[#86868b]">
                  <span className="text-[#86868b] mt-0.5">—</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[15px] font-semibold text-[#1d1d1f] mb-2">How Does M3P Work?</h3>
            <ul className="space-y-1.5">
              {[
                'Patients must opt-in to participate.',
                'Patients pay $0 at the pharmacy for covered medications — the pharmacy does not calculate M3P cost at checkout.',
                'Part D plan sponsors bill patients monthly for cost-sharing related to medication claims.',
                'Pharmacies are reimbursed by the Part D plan sponsor; the patient pays the plan, not the pharmacy.',
              ].map((item) => (
                <li key={item} className="flex gap-2 text-[15px] text-[#86868b]">
                  <span className="text-[#86868b] mt-0.5">—</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[15px] font-semibold text-[#1d1d1f] mb-2">Who Benefits Most?</h3>
            <ul className="space-y-1.5">
              {[
                'Patients with large co-pays or co-insurance who cannot pay their total amount in a single transaction.',
                'Patients with a cost-share of ≥$600 for a single prescription will automatically be identified by Part D sponsors and informed about M3P.',
              ].map((item) => (
                <li key={item} className="flex gap-2 text-[15px] text-[#86868b]">
                  <span className="text-[#86868b] mt-0.5">—</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[15px] font-semibold text-[#1d1d1f] mb-2">How to Opt In</h3>
            <ul className="space-y-1.5">
              {[
                'Patients must contact their Medicare plan via phone, paper form, or online portal.',
                'Pharmacies cannot enroll patients in M3P at the counter.',
                'Confirmation from the insurance plan typically arrives within 24 hours; enrollment is effective the next calendar month.',
              ].map((item) => (
                <li key={item} className="flex gap-2 text-[15px] text-[#86868b]">
                  <span className="text-[#86868b] mt-0.5">—</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Formulas */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold text-[#1d1d1f] mb-2">Monthly Payment Formulas</h2>
          <p className="text-[15px] text-[#86868b] mb-6 leading-relaxed">
            Monthly bills are capped to prevent unaffordable lump sums. The cap is recalculated each month based on remaining balance and months left in the year.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <FormulaCard
              title="First Month Maximum Cap"
              formula={"($2,000 − Prior OOP Paid) ÷ Months Remaining in Year"}
            />
            <FormulaCard
              title="Subsequent Month Maximum Cap"
              formula={"(Current M3P Balance + New OOP This Month) ÷ Months Remaining in Year"}
            />
          </div>
        </div>

        {/* FAQ */}
        <div>
          <h2 className="text-xl font-semibold text-[#1d1d1f] mb-6">Frequently Asked Questions</h2>
          <div className="divide-y divide-[#e5e5ea] border-t border-[#e5e5ea]">
            {faqs.map((item) => (
              <FAQItem key={item.q} q={item.q} a={item.a} />
            ))}
          </div>
        </div>

      </section>

      {/* ── CTA ───────────────────────────────────── */}
      <section className="relative overflow-hidden bg-[#1d1d1f] px-8 md:px-16 py-12">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-blue-600/15 via-purple-600/10 to-transparent blur-3xl pointer-events-none" />
        <div className="relative max-w-[860px] mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="text-xl font-semibold text-white mb-2">Want to learn how MedHug supports M3P?</h3>
            <p className="text-[15px] text-white/50 font-light">Book a 30-minute demo with our team.</p>
          </div>
          <Link
            to={buildSignupPath()}
            state={buildSignupState('m3p-book-demo')}
            data-ph-capture-attribute-cta="m3p-book-demo"
            onClick={() => captureCtaClick('m3p-book-demo', { placement: 'm3p-cta' })}
            className="bg-white text-[#1d1d1f] px-8 py-4 rounded-full text-[15px] font-medium hover:bg-gray-100 transition-all whitespace-nowrap flex-shrink-0 shadow-lg"
          >
            Book a Demo
          </Link>
        </div>
      </section>

    </div>
  );
}
